// The condition visit at the top of the Condition tab: its status (Draft ›
// Complete › Validated › Issued), the inspection date, the visit type, and
// the centre and last year's visit. Completing, validating, reopening and the
// links need signal (lib/visit-lifecycle.ts); the date saves offline.
import { useState } from 'react';
import { Button, ConfirmDialog, Label } from '@higgins/ui';
import { ApiError, getVisitLinksApi, isConnectionFailure, type CentreFromApi, type PastVisitFromApi, type VisitLinksToApi } from '../lib/api';
import type { DeviceRecord } from '../lib/device-db';
import { formatDate } from '../lib/dates';
import { changeVisitLinks, changeVisitStatus, setInspectedOn } from '../lib/visit-lifecycle';
import { SavedTextField } from './saved-text-field';
import { inspectedDay, localDay, nextChanges, VISIT_STEPS, visitStep } from '../lib/visit-steps';

type Change = 'complete' | 'validate' | 'reopen';
const CHANGE_LABEL: Record<Change, string> = { complete: 'Complete visit', validate: 'Validate visit', reopen: 'Reopen' };
const NO_PREVIOUS = '';
const YEAR_DIGITS = 4;

interface Links {
  centres: CentreFromApi[];
  pastVisits: PastVisitFromApi[];
  yearBuilt: number | null;
}

function describe(err: unknown): string {
  if (isConnectionFailure(err)) return 'This needs signal. Try again when you have it.';
  // The client's contract names what must be there first (condition-contract.ts): list it.
  const gaps = err instanceof ApiError && err.code === 'EVIDENCE_MISSING' ? (err.body as { gaps?: unknown }).gaps : null;
  if (Array.isArray(gaps)) return `${(err as ApiError).message} ${gaps.join('; ')}.`;
  if (err instanceof ApiError) return err.message;
  return 'That didn’t work. Try again.';
}

const unratedOf = (err: unknown): string[] | null =>
  err instanceof ApiError && err.code === 'UNRATED_AREAS' && Array.isArray((err.body as { areas?: unknown })?.areas) ? ((err.body as { areas: string[] }).areas) : null;

const pastLabel = (v: PastVisitFromApi) => [v.jobNumber, localDay(v.inspectedOn) ?? 'no date', v.status].filter(Boolean).join(' · ');

export function VisitPanel({ visit }: { visit: DeviceRecord }) {
  const step = visitStep(visit);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [unrated, setUnrated] = useState<string[] | null>(null);
  const [links, setLinks] = useState<Links | null>(null);

  async function run(work: () => Promise<void>) {
    setBusy(true);
    setProblem(null);
    try {
      await work();
    } catch (err) {
      const areas = unratedOf(err);
      if (areas) setUnrated(areas);
      else setProblem(describe(err));
    } finally {
      setBusy(false);
    }
  }

  const change = (c: Change, force = false) => run(async () => {
    await changeVisitStatus(visit, c, force);
    setUnrated(null);
  });
  const showLinks = () => run(async () => setLinks(await getVisitLinksApi(visit.id)));
  const saveLinks = (body: VisitLinksToApi) => run(async () => {
    await changeVisitLinks(visit, body);
    setLinks(await getVisitLinksApi(visit.id));
  });

  const centreId = typeof visit.fields.hig_facilityid === 'string' ? visit.fields.hig_facilityid.toLowerCase() : '';
  const previousId = typeof visit.fields.hig_previousvisitid === 'string' ? visit.fields.hig_previousvisitid.toLowerCase() : NO_PREVIOUS;

  return (
    <div className="mb-3 space-y-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <ol className="flex flex-wrap items-center gap-1 text-xs" aria-label="Visit status">
          {VISIT_STEPS.map((s) => (
            <li key={s} aria-current={s === step ? 'step' : undefined} className={`rounded-full px-2.5 py-1 ${s === step ? 'bg-primary text-primary-foreground' : 'bg-slate-100 text-slate-600'}`}>
              {s}
            </li>
          ))}
        </ol>
        <span className="text-xs text-slate-500">Audit visit</span>
        <Label htmlFor="visit-inspected-on" className="ml-auto text-xs text-slate-500">Inspected on</Label>
        <input
          id="visit-inspected-on"
          type="date"
          value={inspectedDay(visit.fields.hig_inspectedon)}
          disabled={step !== 'Draft'}
          onChange={(e) => e.target.value && void setInspectedOn(visit, e.target.value)}
          className="min-h-11 rounded-md border border-slate-300 px-2 text-sm"
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {nextChanges(step).map((c) => (
          <Button key={c} variant={c === 'reopen' ? 'outline' : 'default'} size="sm" className="min-h-11" disabled={busy} onClick={() => void change(c)}>
            {CHANGE_LABEL[c]}
          </Button>
        ))}
        {!links && (
          <Button variant="outline" size="sm" className="min-h-11" disabled={busy} onClick={() => void showLinks()}>
            Centre and last visit
          </Button>
        )}
        {step === 'Complete' && <span className="text-xs text-slate-500">Completed {localDay(visit.fields.hig_completedon)}. Validating locks the ratings until reopened.</span>}
        {typeof visit.fields.hig_reportdueon === 'string' && step !== 'Issued' && <span className="text-xs text-slate-500">Report due {formatDate(visit.fields.hig_reportdueon)}.</span>}
        {step === 'Validated' && <span className="text-xs text-slate-500">Validated {localDay(visit.fields.hig_validatedon)}. Reopen it to change the ratings.</span>}
      </div>
      {links && (
        <div className="grid gap-2 sm:grid-cols-2">
          <label className="text-xs text-slate-500">
            Centre
            <select className="mt-1 block min-h-11 w-full rounded-md border border-slate-300 px-2 text-sm text-slate-900" value={centreId} disabled={busy} onChange={(e) => void saveLinks({ facilityId: e.target.value })}>
              {!links.centres.some((c) => c.id.toLowerCase() === centreId) && <option value={centreId}>This job’s centre</option>}
              {links.centres.map((c) => (
                <option key={c.id} value={c.id.toLowerCase()}>{c.address ? `${c.name} — ${c.address}` : c.name}</option>
              ))}
            </select>
          </label>
          <label className="text-xs text-slate-500">
            Last visit to this centre
            <select className="mt-1 block min-h-11 w-full rounded-md border border-slate-300 px-2 text-sm text-slate-900" value={previousId} disabled={busy} onChange={(e) => void saveLinks({ previousVisitId: e.target.value || null })}>
              <option value={NO_PREVIOUS}>{links.pastVisits.length ? 'None' : 'No earlier visits'}</option>
              {links.pastVisits.map((v) => (
                <option key={v.id} value={v.id.toLowerCase()}>{pastLabel(v)}</option>
              ))}
            </select>
          </label>
          <div className="text-xs text-slate-500">
            <Label htmlFor="centre-year-built" className="text-xs text-slate-500">Year the centre was built (approximate is fine)</Label>
            <SavedTextField
              id="centre-year-built"
              label="Year built"
              value={links.yearBuilt === null ? '' : String(links.yearBuilt)}
              onSave={(year) => saveLinks({ yearBuilt: year.trim() ? Number(year.trim()) : null })}
              placeholder="e.g. 1998"
              maxLength={YEAR_DIGITS}
              className="mt-1"
            />
          </div>
        </div>
      )}
      {problem && <p className="text-sm text-red-700">{problem}</p>}
      <ConfirmDialog
        open={unrated !== null}
        onOpenChange={(open) => !open && setUnrated(null)}
        title="Complete with areas not rated?"
        description={`Nothing is rated on: ${(unrated ?? []).join(', ')}. Their cards will read “Not rated”.`}
        confirmLabel="Complete anyway"
        busyLabel="Completing…"
        busy={busy}
        onConfirm={() => void change('complete', true)}
      />
    </div>
  );
}
